import { useCallback, useState } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import { downloadBase64AsPDF } from "@lib/base64-to-file";
import scrollToTheTop from "@lib/scrollToTheTop";
import joinDefinedStringParts from "@utils/joinDefinedStringParts";

import { ORDER_MESSAGE_ZONE } from "../../constants";
import { loadShipmentLabel } from "../../services/loadShipmentLabel";

type TOrderLabelDownload = {
  isLoading: boolean;
  downloadOrderLabel: (shipmentId: string, parcelNo?: string) => void;
};

const useOrderLabelDownload = (): TOrderLabelDownload => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const setMessage = useMessage();

  const handleLabelError = useCallback(() => {
    setMessage({
      message: t("order-message-label_download_failed"),
      props: {
        variant: "error",
        icon: <Icon icon="warning" size="1.45rem" />,
        canClose: true,
        padding: "0.45rem 0.75rem",
      },
      zone: ORDER_MESSAGE_ZONE,
    });
    scrollToTheTop();
  }, [setMessage, t]);

  const downloadOrderLabel = useCallback(
    async (shipmentId: string, parcelNo?: string) => {
      setIsLoading(true);

      const result = await loadShipmentLabel(shipmentId);

      if (!result.isSuccess || !result.data) {
        handleLabelError();
        setIsLoading(false);
        return;
      }

      const fileName = joinDefinedStringParts(["DPD-label", parcelNo], "-");

      downloadBase64AsPDF(result.data, fileName, handleLabelError);
      setIsLoading(false);
    },
    [handleLabelError]
  );

  return { isLoading, downloadOrderLabel };
};
export default useOrderLabelDownload;
